/**
 * Время расписания и занятость как чистая арифметика: интервалы, пересечения, срок напоминания.
 *
 * Раньше этот расчёт лежал внутри серверного модуля расписания, а веб для подсветки занятости
 * площадки считал пересечение сам — той же формулой, написанной второй раз. Расхождение таких
 * копий выглядит как «интерфейс показал свободно, сервер ответил 409», поэтому формула здесь
 * одна и общая для обеих сторон.
 */
export function parseIsoMs(value) {
    const ms = Date.parse(value);
    if (!Number.isFinite(ms)) {
        throw new Error(`Invalid ISO datetime: ${value}`);
    }
    return ms;
}
export function addMinutesIso(iso, minutes) {
    return new Date(parseIsoMs(iso) + minutes * 60_000).toISOString();
}
export function lessonEndIso(startIso, durationMinutes) {
    return addMinutesIso(startIso, durationMinutes);
}
/** Занятость инструктора: [начало, конец + буфер) — буфер на переезд между площадками. */
export function instructorOccupancyInterval(startIso, durationMinutes, bufferMinutes) {
    const startMs = parseIsoMs(startIso);
    const endMs = startMs + (durationMinutes + bufferMinutes) * 60_000;
    return { startMs, endMs };
}
/** Занятость площадки: [начало, конец) — буфера у неё нет. */
export function venueOccupancyInterval(startIso, durationMinutes) {
    const startMs = parseIsoMs(startIso);
    const endMs = startMs + durationMinutes * 60_000;
    return { startMs, endMs };
}
/** Пересекаются ли полуинтервалы: касание границами пересечением не считается. */
export function halfOpenOverlap(a, b) {
    return a.startMs < b.endMs && b.startMs < a.endMs;
}
export const REMINDER_LEAD_MS = 2 * 60 * 60 * 1000;
export function reminderRunAtIso(startsAt) {
    return new Date(parseIsoMs(startsAt) - REMINDER_LEAD_MS).toISOString();
}
/**
 * Состояния, при которых урок ещё занимает место. Список типизирован от `LessonState`:
 * раньше это были просто строки, и новое нетерминальное состояние молча выпало бы из проверки
 * конфликтов — то есть двойная запись на площадку прошла бы как свободная.
 */
export const NON_TERMINAL_LESSON_STATUSES = Object.freeze([
    "scheduled",
    "client_arrived",
]);
export function isNonTerminalLessonStatus(status) {
    return NON_TERMINAL_LESSON_STATUSES.includes(status);
}
/**
 * Курс в план уроков (§3.2 docs/amo-architecture.md).
 *
 * `Уроков` (в amo поле до сих зовётся `Дней`) — количество занятий, `Минут на урок` — длина
 * слота. Когда заполнен только общий объём, план делит его ровно: недонастроенная карточка
 * курса всё равно даёт рабочий план вместо ошибки.
 */
export function buildCourseLessonPlan(course) {
    const count = Math.max(1, Math.trunc(course.lessonCount));
    const perLesson = course.minutesPerLesson > 0
        ? course.minutesPerLesson
        : course.minutesTotal > 0
            ? Math.round(course.minutesTotal / count)
            : 0;
    if (perLesson <= 0)
        return [];
    return Array.from({ length: count }, (_, index) => ({
        lessonOrder: index + 1,
        title: `${course.name} — занятие ${index + 1}`,
        durationMinutes: perLesson,
    }));
}
/**
 * Доп. опция, а не курс: у карточки нет ни уроков, ни минут, и плана из неё не построить.
 *
 * Так устроены «Права» (SKU `rights`): это услуга с ценой, которую докупают к курсу, а не
 * обучение. Раньше из такой карточки получался пустой «бессрочный» план — зачисление без единого
 * урока, которое шторка сделки потом предлагала «докупить» и «записать». Опция остаётся в сделке
 * и в контракте как строка с ценой; уроков у неё не бывает.
 */
export function isAddonCourse(course) {
    return buildCourseLessonPlan(course).length === 0;
}
